/** @jsxImportSource @emotion/react */
import { Header } from '@quickbit/backoffice-core-frontend';
import {
  SearchInput,
  Table,
  Typography,
  useForm,
  useTheme,
  UtilityButton,
  DateInput,
  Pagination,
} from '@quickbit/qb-design-system';
import { useMemo, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { validate as validateUUID } from 'uuid';
import {
  SearchFrozenAssetsFilters,
  useFrozenAssetsApi,
} from 'components/FrozenAssets/api';

const PAGE_SIZE = 16;

export const FrozenAssetList = () => {
  const { spacing } = useTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const headers = [
    { label: 'transactionId', cellKey: 'transactionId', sort: false },
    { label: 'reason', cellKey: 'reason', sort: false },
    { label: 'date', cellKey: 'date', sort: false },
  ];

  const { fields, setters } = useForm<SearchFrozenAssetsFilters>(
    {
      transactionId: searchParams.get('transactionId') || undefined,
      reason: searchParams.get('reason') || undefined,
      createdFrom: searchParams.get('createdFrom') || undefined,
      createdTo: searchParams.get('createdTo') || undefined,
    },
    {}
  );

  const page = parseInt(searchParams.get('page') || '1', 10);

  useEffect(() => {
    const params = new URLSearchParams();
    if (fields.transactionId) params.set('transactionId', fields.transactionId);
    if (fields.reason) params.set('reason', fields.reason);
    if (fields.createdFrom) params.set('createdFrom', fields.createdFrom);
    if (fields.createdTo) params.set('createdTo', fields.createdTo);
    params.set('page', page.toString());
    setSearchParams(params);
  }, [fields, page, setSearchParams]);

  const appliedFilters = {
    filters: {
      ...fields,
      transactionId:
        fields.transactionId && validateUUID(fields.transactionId)
          ? fields.transactionId
          : undefined,
      reason: fields.reason || undefined,
      createdFrom: fields.createdFrom,
      createdTo: fields.createdTo,
    },
    pagination: {
      page: page,
      size: PAGE_SIZE,
    },
  };

  const { useGetFrozenAssets } = useFrozenAssetsApi();
  const { data } = useGetFrozenAssets(
    appliedFilters.filters,
    appliedFilters.pagination
  );

  const rows = useMemo(() => {
    if (!data) {
      return [];
    }
    return data.frozenAssets.map((asset) => {
      return {
        value: asset as unknown as Record<string, string>,
        id: asset.transactionId,
      };
    });
  }, [data]);

  const handleResetFilters = () => {
    setters.set({
      transactionId: undefined,
      reason: undefined,
      createdFrom: undefined,
      createdTo: undefined,
    });
    setSearchParams({ page: '1' });
  };

  return (
    <>
      <Header>
        <div
          css={{
            display: 'grid',
            gridTemplateColumns: '1fr repeat(1, auto)',
            alignItems: 'center',
            gap: spacing.xxs,
          }}
        >
          <Typography variant="subtitle">Frozen Assets</Typography>
        </div>
      </Header>
      <div
        css={{
          display: 'grid',
          gridTemplateColumns: 'auto auto auto auto auto 1fr',
          gap: spacing.xxxs,
          alignItems: 'center',
          zIndex: 1,
        }}
      >
        <SearchInput
          placeholder="Transaction id"
          value={fields.transactionId ?? ''}
          onChange={(e) => {
            setters.transactionId?.set(e.target.value);
          }}
          error={
            fields.transactionId !== undefined &&
            fields.transactionId !== '' &&
            !validateUUID(fields.transactionId)
          }
        />
        <SearchInput
          placeholder="Reason"
          value={fields.reason ?? ''}
          onChange={(e) => {
            setters.reason?.set(e.target.value);
          }}
        />
      </div>
      <div
        css={{
          marginTop: spacing.xxs,
          marginBottom: spacing.xxs,
          display: 'grid',
          gridTemplateColumns: 'repeat(99, auto) 1fr',
          alignItems: 'center',
          gap: spacing.xxxs,
          textWrap: 'nowrap',
        }}
      >
        <DateInput
          placeholder="All time"
          type="range"
          value={[
            fields.createdFrom ? new Date(fields.createdFrom) : null,
            fields.createdTo ? new Date(fields.createdTo) : null,
          ]}
          onChange={(e: [Date | null, Date | null]) => {
            setters.createdFrom?.set(e[0]?.toISOString());
            setters.createdTo?.set(e[1]?.toISOString());
          }}
          withTime
        />
        <UtilityButton
          variant="text"
          text="Reset filters"
          onClick={handleResetFilters}
        />
      </div>
      <Table
        headers={headers}
        rows={rows}
        // eslint-disable-next-line @typescript-eslint/no-base-to-string, @typescript-eslint/restrict-template-expressions
        onRowClick={(row) => navigate(`/transactions/${row.transactionId}`)}
      />
      <div css={{ marginTop: spacing.xxs }}>
        <Pagination
          currentPage={page}
          onChange={(newPage) => {
            setSearchParams((prev) => {
              prev.set('page', newPage.toString());
              return prev;
            });
          }}
          numberOfElements={data?.aggregatedData.count || 0}
          pageSize={PAGE_SIZE}
        />
      </div>
    </>
  );
};
